<script>
import { mapActions, mapMutations, mapState } from 'vuex'

export default {
  name: 'SellerCard',

  props: {
    product: {
      type: Object,
      required: true
    },

    noChat: {
      type: Boolean,
      default: false
    },

    light: {
      type: Boolean,
      default: false
    }
  },

  computed: {
    ...mapState('user', ['user']),

    avatar () {
      return this.product?.avatar
    },

    sellerName () {
      return this.product?.full_name || '-'
    },

    sellerProfileLink () {
      return this.localeLocation(`/user/${this.product?.alias}`)
    },

    sellerId () {
      return this.product?.account_id
    },

    iAmSeller () {
      return this.user?.id === this.sellerId
    },

    rating () {
      return +(this.product.rating || 0)
    },

    roundedRating () {
      console.log('rating', this.rating)
      return Math.round(this.rating)
    },

    reviewsAmount () {
      return this.product.reviews_amount
    },

    newSeller () {
      return this.reviewsAmount < 5
    },

    isAuto () {
      return this.product.auto_messages === '1'
    }
  },

  methods: {
    ...mapMutations('chat', ['setChatId', 'setChatName']),
    ...mapActions('modal', ['showModal']),

    openRegistrationModal () {
      this.showModal({
        component: 'Auth',
        data: {
          mode: 'register'
        }
      })
    },

    openChat () {
      if (!this.user) {
        this.openRegistrationModal()
        return
      }

      const chatId = [this.user?.id, this.sellerId].sort().join('_')

      this.setChatId(chatId)
      this.setChatName(this.sellerName)

      // this.$gtag.event('chat_click', { account_id: this.user?.id, place: 'product_page' })
    }
  }
}
</script>

<template>
  <div :class="{ [$style.dealer]: true, [$style.dealerLight]: light }">
    <NuxtLink :class="$style.dealer__top" :to="sellerProfileLink">
      <img :class="$style.dealer__topAvatar" :src="avatar" alt="avatar" />
      <div :class="$style.dealer__topRight">
        <div :class="$style.dealer__topRightTop">
          <div :class="$style.dealer__topName">
            {{ sellerName }}
          </div>
        </div>
        <div :class="$style.dealer__topRightBottom">
          <div v-if="newSeller" :class="$style.newSeller">
            {{ $t("newSeller") }}
          </div>
          <template v-else>
            <div :class="$style.stars">
              <img v-for="i in roundedRating" :key="`star_${i}`" :class="$style.star" :src="require('~/assets/img/icons/star-gold.svg')" alt="star" />
              <img v-for="i in (5 - roundedRating)" :key="`no_star_${i}`" :class="$style.star" :src="require('~/assets/img/icons/star-grey.svg')" alt="star empty" />
            </div>
            <div :class="$style.dealer__topReviews">
              {{ $t("nReviews", { amount: reviewsAmount }) }}
            </div>
          </template>
        </div>
      </div>
    </NuxtLink>

    <div v-if="!iAmSeller && !noChat">
      <div :class="$style.dealer__chat" @click="openChat">
        <img :class="$style.dealer__chatIcon" :src="require('~/assets/img/icons/chatvvv.svg')" alt="chat" />
        <span :class="$style.dealer__chatWith">
          {{ $t('chatWith') }}&nbsp;
          <span :class="$style.dealer__chatWithName">
            {{ sellerName }}
          </span>
        </span>
      </div>

      <p v-if="isAuto" :class="$style.info">
        {{ $t("dontNeedChatWithSeller") }}
      </p>
    </div>
  </div>
</template>

<style lang="scss" module>
.dealer {
  padding: 12px;
  background: $blue-grey70;
  border-radius: 8px;

  @media (min-width: $breakpointDesktopWide) {
    width: 100%;
    padding: 32px;
  }

  &Light {
    background: $blue-grey60;
  }

  &__chat {
    margin-top: 12px;
    width: 100%;
    padding: 8px 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $white;
    background: $blue-grey50;
    border-radius: 8px;
    cursor: pointer;

    &With {
      font-size: 16px;
      font-style: normal;
      font-weight: 500;
      line-height: 20px;

      &Name {
        color: $amber60;
      }
    }

    &Icon {
      width: 24px;
      height: 24px;
      margin-right: 8px;
    }
  }

  &__top {
    display: grid;
    grid-template-columns: max-content 1fr;
    grid-gap: 8px;

    &Right {
      display: grid;
      grid-template-columns: 1fr;
      grid-gap: 4px;

      &Bottom {
        display: flex;
        align-items: center;
        justify-content: flex-start;
      }
    }

    &Name {
      color: $white;
      font-size: 16px;
      font-weight: 500;
      line-height: 20px;
    }

    &Avatar {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      object-fit: cover;
    }

    &Reviews {
      color: $blue-grey10;
      font-size: 12px;
      font-weight: 400;
      line-height: 20px;
    }

    &Price {
      color: $white;
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
    }
  }
}

.newSeller {
  background: $amber80;
  color: $white;
  border-radius: 100px;
  padding: 0 8px;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
}

.stars {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-right: 8px;
}

.star {
  width: 16px;
  height: 16px;
}

.info {
  font-size: 16px;
  line-height: 120%;
  font-weight: 700;
  margin: 10px 0;
  padding: 16px;
  background: $blue-grey80;
  color: $blue-grey05;
  border-radius: 8px;
  letter-spacing: 0.4px;
}
</style>
